// src/mock/mockData.js

// Seed for reproducible "random" data
const seededRandom = (seed) => {
    const x = Math.sin(seed++) * 10000;
    return x - Math.floor(x);
};

// Helper function to generate UUID-like strings
const generateId = (seed) => {
    const hex = (n) => Math.floor(seededRandom(seed + n) * 16).toString(16);
    return `${hex(0)}${hex(1)}${hex(2)}${hex(3)}${hex(4)}${hex(5)}${hex(6)}${hex(7)}-${hex(8)}${hex(9)}${hex(10)}${hex(11)}-4${hex(12)}${hex(13)}${hex(14)}-${hex(15)}${hex(16)}${hex(17)}${hex(18)}-${hex(19)}${hex(20)}${hex(21)}${hex(22)}${hex(23)}${hex(24)}${hex(25)}${hex(26)}${hex(27)}${hex(28)}${hex(29)}${hex(30)}`;
};

// Base users
export const mockUsers = [
    {
        username: 'e830341c-7818-4f5f-9d02-85f65f3e6c9a',
        email: 'mans@netcompany.com',
        firstName: 'Magnus',
        lastName: 'Nielsen',
        profilePicture: '/images/profiles/mans.jpg'
    },
    {
        username: 'd0dd960e-4bef-4cb6-a41a-2819afe6fb35',
        email: 'abkl@netcompany.com',
        firstName: 'Abel',
        lastName: 'Klein',
        profilePicture: '/images/profiles/abkl.jpg'
    },
    {
        username: 'c258b71c-3817-4c07-a744-a5c12b13c256',
        email: 'oskbl@netcompany.com',
        firstName: 'Oscar',
        lastName: 'Blom',
        profilePicture: '/images/profiles/oskbl.jpg'
    },
    {
        username: 'a1b2c3d4-1234-5678-9abc-def012345678',
        email: 'emma@netcompany.com',
        firstName: 'Emma',
        lastName: 'Andersen',
        profilePicture: null
    },
    {
        username: 'b2c3d4e5-2345-6789-abcd-ef0123456789',
        email: 'lucas@netcompany.com',
        firstName: 'Lucas',
        lastName: 'Pedersen',
        profilePicture: null
    },
    {
        username: 'c3d4e5f6-3456-789a-bcde-f01234567890',
        email: 'sofia@netcompany.com',
        firstName: 'Sofia',
        lastName: 'Jensen',
        profilePicture: null
    },
    {
        username: 'd4e5f6a7-4567-89ab-cdef-012345678901',
        email: 'oliver@netcompany.com',
        firstName: 'Oliver',
        lastName: 'Hansen',
        profilePicture: null
    },
    {
        username: 'e5f6a7b8-5678-9abc-def0-123456789012',
        email: 'ida@netcompany.com',
        firstName: 'Ida',
        lastName: 'Larsen',
        profilePicture: null
    }
];

// Activity types with their characteristics
const activityTypes = [
    { type: 'running', minDistance: 2, maxDistance: 25, minElevation: 0, maxElevation: 500, minDuration: 600, maxDuration: 10800 },
    { type: 'cycling', minDistance: 10, maxDistance: 100, minElevation: 0, maxElevation: 1500, minDuration: 1200, maxDuration: 18000 },
    { type: 'swimming', minDistance: 0.5, maxDistance: 5, minElevation: 0, maxElevation: 0, minDuration: 600, maxDuration: 5400 },
    { type: 'hiking', minDistance: 5, maxDistance: 30, minElevation: 100, maxElevation: 2000, minDuration: 3600, maxDuration: 28800 },
    { type: 'walking', minDistance: 2, maxDistance: 15, minElevation: 0, maxElevation: 200, minDuration: 1200, maxDuration: 7200 }
];

// Helper function to calculate points based on activity type
export const calculatePoints = (activity) => {
    let points = 0;
    switch (activity.activityType) {
        case 'running':
            points = activity.distance + (activity.elevation / 100);
            break;
        case 'cycling':
            points = (activity.distance * 0.25) + (activity.elevation / 200);
            break;
        case 'swimming':
            points = activity.distance * 4;
            break;
        case 'hiking':
            points = (activity.distance * 0.5) + (activity.elevation / 100 * 0.5);
            break;
        case 'walking':
            points = (activity.distance * 0.3) + (activity.elevation / 150);
            break;
        default:
            points = activity.distance;
    }
    return Number(points.toFixed(2));
};

// Generate random number in range using seed
const randomInRange = (min, max, seed) => {
    return min + seededRandom(seed) * (max - min);
};

// Generate activities dynamically
const generateActivities = () => {
    const activities = [];
    const now = new Date();
    let activityId = 1;

    // Generate activities for the past 18 months
    for (let monthsAgo = 0; monthsAgo < 18; monthsAgo++) {
        // Each user gets 2-8 activities per month
        mockUsers.forEach((user, userIndex) => {
            const activitiesThisMonth = Math.floor(randomInRange(2, 9, userIndex * 100 + monthsAgo));

            for (let i = 0; i < activitiesThisMonth; i++) {
                const seed = activityId * 1000 + userIndex * 100 + monthsAgo * 10 + i;
                const activityTypeIndex = Math.floor(randomInRange(0, activityTypes.length, seed));
                const actType = activityTypes[activityTypeIndex];

                const distance = Number(randomInRange(actType.minDistance, actType.maxDistance, seed + 1).toFixed(2));
                const elevation = Math.floor(randomInRange(actType.minElevation, actType.maxElevation, seed + 2));
                const duration = Math.floor(randomInRange(actType.minDuration, actType.maxDuration, seed + 3));

                // Random day within the month
                const date = new Date(now);
                date.setMonth(date.getMonth() - monthsAgo);
                date.setDate(Math.floor(randomInRange(1, 28, seed + 4)));
                date.setHours(Math.floor(randomInRange(6, 20, seed + 5)), 0, 0, 0);

                const activity = {
                    activityId: String(activityId),
                    activityType: actType.type,
                    distance,
                    elevation,
                    date: date.toISOString(),
                    duration,
                    username: user.username,
                    points: 0
                };
                activity.points = calculatePoints(activity);

                // Add description to some activities
                if (seededRandom(seed + 6) > 0.7) {
                    const descriptions = [
                        'Great workout today!',
                        'Felt strong throughout',
                        'Recovery session',
                        'Pushed hard on the hills',
                        'Beautiful weather for this',
                        'Personal best attempt',
                        'Easy pace today',
                        'Group session with friends',
                        'Morning routine',
                        'Evening stress relief'
                    ];
                    activity.description = descriptions[Math.floor(randomInRange(0, descriptions.length, seed + 7))];
                }

                // Add calories to some activities
                if (seededRandom(seed + 8) > 0.3) {
                    activity.calories = Math.floor(activity.points * randomInRange(30, 50, seed + 9));
                }

                activities.push(activity);
                activityId++;
            }
        });
    }

    return activities.sort((a, b) => new Date(b.date) - new Date(a.date));
};

// Competition name templates
const competitionNames = [
    'Minister of Activity',
    'Spring Challenge',
    'Summer Showdown',
    'Fall Fitness Frenzy',
    'Winter Warriors',
    'New Year Resolution',
    'March Madness Fitness',
    'Holiday Hustle',
    'Quarterly Quest',
    'Monthly Motivation'
];

// Generate competitions dynamically
const generateCompetitions = (activities) => {
    const competitions = [];
    const now = new Date();
    let competitionId = 1;

    // Generate competitions for the past 12 months and 2 months into the future
    for (let monthOffset = -12; monthOffset <= 2; monthOffset++) {
        const seed = competitionId * 500 + monthOffset;

        // 1-2 competitions per month
        const competitionsThisMonth = monthOffset === 0 ? 2 : Math.floor(randomInRange(1, 3, seed));

        for (let i = 0; i < competitionsThisMonth; i++) {
            const compSeed = seed + i * 100;

            const startDate = new Date(now);
            startDate.setMonth(startDate.getMonth() + monthOffset);
            startDate.setDate(i === 0 ? 1 : 15);
            startDate.setHours(0, 0, 0, 0);

            // Competition duration: 2-6 weeks
            const durationWeeks = Math.floor(randomInRange(2, 7, compSeed));
            const endDate = new Date(startDate);
            endDate.setDate(endDate.getDate() + durationWeeks * 7);

            // Determine state based on dates
            let state;
            if (endDate < now) {
                state = 'concluded';
            } else if (startDate > now) {
                state = 'upcoming';
            } else {
                state = 'active';
            }

            // Select random competitors (3-8 users)
            const numCompetitors = Math.floor(randomInRange(3, Math.min(9, mockUsers.length + 1), compSeed + 1));
            const shuffledUsers = [...mockUsers].sort(() => seededRandom(compSeed + 2) - 0.5);
            const competitors = shuffledUsers.slice(0, numCompetitors).map(u => u.username);

            // Calculate points for each competitor in this competition
            const competitorPoints = competitors.map(username => {
                const userActivities = activities.filter(a =>
                    a.username === username &&
                    new Date(a.date) >= startDate &&
                    new Date(a.date) <= endDate
                );
                return {
                    username,
                    totalPoints: userActivities.reduce((sum, act) => sum + act.points, 0)
                };
            }).sort((a, b) => b.totalPoints - a.totalPoints);

            // Minister is the top scorer
            const minister = competitorPoints[0] || { username: competitors[0], totalPoints: 0 };

            // Point target is based on average or set value
            const avgPoints = competitorPoints.reduce((sum, c) => sum + c.totalPoints, 0) / competitorPoints.length;
            const pointTarget = Number((avgPoints * randomInRange(0.6, 0.9, compSeed + 3)).toFixed(2)) || 25;

            const nameIndex = Math.floor(randomInRange(0, competitionNames.length, compSeed + 4));

            competitions.push({
                name: competitionNames[nameIndex],
                competitionId,
                startDate: startDate.toISOString(),
                endDate: endDate.toISOString(),
                state,
                ministerOfActivity: {
                    username: minister.username,
                    lastRoundPoints: Number(minister.totalPoints.toFixed(2))
                },
                competitors,
                pointTarget
            });

            competitionId++;
        }
    }

    return competitions.sort((a, b) => new Date(b.startDate) - new Date(a.startDate));
};

// Generate the mock data
export const mockActivities = generateActivities();
export const mockCompetitions = generateCompetitions(mockActivities);
